import React, { useState, useMemo } from 'react';
import { finalize } from 'rxjs/operators';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/changeNumberDialogStyle';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { UsersService } from 'services/users/users';
import { setToast } from 'redux/modules/toast/actions';
import ChangePasswordWrapper from '../common/ChangeNumberWrapper';
import ChangeEmailTab from './tabs/ChangeEmailTab';
import OTP from 'components/OTP/OTP';
import {
	upIdSelector,
	userContactNumberSelector,
	userIdSelector,
	userNameSelector,
} from 'redux/modules/session/selectors';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';
import { getEditEmailLabels, getOTPScreenLabels } from 'redux/modules/dynamicLabels/selectors';
import { BodyM } from 'components/Typography/Typography';
import { getLoadingIndicator } from 'redux/modules/api/loadingIndicator/selector';
import Loader from 'components/Loader/Loader';
import ErrorStatusDialog from 'components/StatusDialog/ErrorStatusDialog';
import SuccessStatusDialog from 'components/StatusDialog/SuccessStatusDialog';
import { WRONG_OTP } from 'utils/const/otp';
import ErrorMapper from 'utils/api/errors/errorMapper';
import OtpTab from 'views/Numbers/Details/Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';

// @ts-ignore
const useStyles = makeStyles(styles);

interface FormData {
	newEmail: string;
}

interface IChangeEmailDialog {
	hideChangeEmail: () => void;
	msisdn: string;
}

const steps = {
	CHANGEEMAIL: 0,
	OTP: 1,
};

const ChangeEmailDialog = (props: IChangeEmailDialog) => {
	const [isResetSuccessful, setIsResetSuccessful] = useState(true);
	const [formData, setFormData] = useState<FormData>({
		newEmail: '',
	});
	const [activeTab, setActiveTab] = useState(steps.CHANGEEMAIL);
	const [otp, setOtp] = useState<string>('');

	const dispatch = useDispatch();
	const isNewDashBoardEnabled = useNewTheme();
	const classes = useStyles({ isNewDashBoardEnabled });

	const userId = useSelector(userIdSelector);
	const userName = useSelector(userNameSelector);
	const upId = useSelector(upIdSelector);
	const userContactNumber = useSelector(userContactNumberSelector);

	const changeEmailLabels = useSelector(getEditEmailLabels);
	const isLoaderVisible = useSelector(getLoadingIndicator);

	const [serverErrorMessage, setServerErrorMessage] = useState('');
	const [isEditSuccess, setIsEditSuccess] = useState(false);
	const [isWrongOtp, setWrongOtp] = useState(false);
	const otpScreenLabels = useSelector(getOTPScreenLabels);

	const getUpdatedUserProfile = () => {
		UsersService.getUserProfile()
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				if (isNewDashBoardEnabled) {
					setIsEditSuccess(true);
					setWrongOtp(false);
				} else {
					dispatch(
						setToast({
							error: false,
							label: translate('change.email.successfulChange'),
						})
					);
				}
			});
	};

	const requestForOtp = (callback = () => {}) => {
		dispatch(showLoadingIndicator());
		setOtp('');
		setWrongOtp(false);
		UsersService.requestForOtp(OperationContextEnum.CORPSUPERADMINCHANGEEMAIL, props.msisdn, isNewDashBoardEnabled)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				() => {
					callback();
					setServerErrorMessage('');
					setToast({
						error: false,
						label: translate('otp.token.sent'),
					});
				},
				(error) => {
					if (isNewDashBoardEnabled) {
						setServerErrorMessage(ErrorMapper.mapToMessage(error.response.code));
					}
				}
			);
	};

	const onSubmit = () => {
		if (checkFormValidity) {
			dispatch(showLoadingIndicator());
			UsersService.updateCorpUserInformation(
				{
					userId: userId,
					email: formData.newEmail,
					username: userName,
					contactNumber: userContactNumber,
					accountId: upId,
					token: otp,
				},
				isNewDashBoardEnabled
			)
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(
					() => {
						if (!isNewDashBoardEnabled) {
							props.hideChangeEmail();
						}
						getUpdatedUserProfile();
					},
					(error) => {
						if (isNewDashBoardEnabled) {
							if (error.response?.code === WRONG_OTP) {
								setWrongOtp(true);
							} else {
								setServerErrorMessage(ErrorMapper.mapToMessage(error.response.code));
							}
						} else {
							if (error.response?.code === WRONG_OTP) {
								setWrongOtp(true);
							} else {
								dispatch(
									setToast({
										error: true,
										label: translate(
											error.response.code === 'DUPLICATE_EMAIL_ADDRESSES'
												? 'change.email.duplicate.address'
												: 'change.email.unsuccessfulEmailChange'
										),
									})
								);
								props.hideChangeEmail();
							}
						}
					}
				);
		} else {
			setIsResetSuccessful(false);
		}
	};
	const onChange = (name: keyof FormData, value: string) =>
		setFormData((formDataState) => ({ ...formDataState, [name]: value }));

	const isFormValid = useMemo(() => Object.keys(formData).every((key) => !!formData[key]), [formData]);

	const checkFormValidity = useMemo(() => {
		return !emailNotValid(formData.newEmail);
	}, [formData]);

	const nextStepAction = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case steps.CHANGEEMAIL:
					return activeTab + 1;
				case steps.OTP:
					return activeTab;
				default:
					return activeTab + 1;
			}
		});
	};

	const getOtpRequest = () => {
		setActiveTab(steps.CHANGEEMAIL);
		requestForOtp(nextStepAction);
	};

	const cancelAction = () => {
		switch (activeTab) {
			case steps.CHANGEEMAIL:
				return props.hideChangeEmail();
			case steps.OTP:
				setOtp('');
				setWrongOtp(false);
				return setActiveTab(activeTab - 1);
		}
	};

	const nextStepDisabled = useMemo(() => {
		switch (activeTab) {
			case steps.CHANGEEMAIL:
				return !checkFormValidity;
			case steps.OTP:
				return otp === '';
		}
	}, [activeTab, checkFormValidity, otp]);

	const submitButtonLabel = isNewDashBoardEnabled ? translate('submit') : translate('change.email.change.email');

	return (
		<CustomDialog
			isOpen
			hideDialog={props.hideChangeEmail}
			dialogBody={
				<>
					{isLoaderVisible && isNewDashBoardEnabled && <Loader />}
					{isNewDashBoardEnabled && serverErrorMessage && (
						<ErrorStatusDialog onClose={props.hideChangeEmail} errorMessage={serverErrorMessage} />
					)}
					{isNewDashBoardEnabled && isEditSuccess && (
						<SuccessStatusDialog operationType="email" onClose={props.hideChangeEmail} />
					)}
					<ChangePasswordWrapper
						tabs={[
							{
								name: translate('change.email.change.email'),
								title: translate('change.email.change.email'),
								content: (
									<ChangeEmailTab
										formData={formData}
										isResetSuccessful={isResetSuccessful}
										isValid={isFormValid}
										onChange={onChange}
										isNewDashBoardEnabled={isNewDashBoardEnabled}
										changeEmailLabels={changeEmailLabels}
									/>
								),
							},
							{
								name: translate('change.number.verification'),
								title: translate('change.number.verification'),
								content: (
									<div className={classes.otpWrapper}>
										{isNewDashBoardEnabled ? (
											<BodyM color="primary-light" customClassName={classes.otpWrapperText}>
												{otpScreenLabels?.editEmailHeader}
											</BodyM>
										) : null}
										{isNewDashBoardEnabled ? (
											<OTP
												otpValue={otp}
												setOtpValue={setOtp}
												isNewDashBoardEnabled={isNewDashBoardEnabled}
												resendOtp={getOtpRequest}
												otpIncorrect={isWrongOtp}
												submitOTP={onSubmit}
											/>
										) : (
											<OtpTab
												otp={otp}
												setOtp={setOtp}
												sendOtp={requestForOtp}
												otpIncorrect={isWrongOtp}
												inOneInput={false}
												submitOTP={onSubmit}
											/>
										)}
									</div>
								),
							},
						]}
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						buttons={[
							{
								text: activeTab === steps.CHANGEEMAIL ? translate('cancel') : translate('back'),
								onClick: cancelAction,
								color: isNewDashBoardEnabled
									? 'tertiary-light-v2'
									: ('tertiary-light' as types.theme.buttonColor),
							},
							{
								text: activeTab === steps.OTP ? submitButtonLabel : translate('next'),
								onClick: activeTab !== steps.OTP ? getOtpRequest : onSubmit,
								color: isNewDashBoardEnabled
									? 'primary-light-v2'
									: ('primary-light' as types.theme.buttonColor),
								disabled: nextStepDisabled,
								id: 'next-button',
							},
						]}
						isNewDashBoardEnabled={isNewDashBoardEnabled}
					/>
				</>
			}
			header={translate('change.email.change.email')}
			width={isNewDashBoardEnabled ? 545 : undefined}
			isNewDashBoardEnabled={isNewDashBoardEnabled}
		/>
	);
};

export default ChangeEmailDialog;
